// lib/recon/drug-dictionary.ts
//
// RxNorm display-term dictionary for the Trial Pipeline competitor picker.
// Fetches the full RxNav display-name list once per session (module cache),
// filters locally as the user types, and best-effort resolves a selected
// brand/display term to an ingredient (generic) name via RxNav.

const RXNAV_DISPLAY_NAMES = "https://rxnav.nlm.nih.gov/REST/displaynames.json"
const RXNAV_RXCUI = "https://rxnav.nlm.nih.gov/REST/rxcui.json"
const RXNAV_RELATED = "https://rxnav.nlm.nih.gov/REST/rxcui"

const DEFAULT_SUGGESTION_LIMIT = 8

type DisplayNamesResponse = {
  displayTermsList?: { term?: string[] }
}

type RxcuiResponse = {
  idGroup?: { rxnormId?: string[] }
}

type RelatedResponse = {
  relatedGroup?: {
    conceptGroup?: Array<{
      tty?: string
      conceptProperties?: Array<{ name?: string }>
    }>
  }
}

let displayTermsCache: string[] | null = null
let displayTermsLoad: Promise<string[]> | null = null

function getDictionaryUrl(): string {
  if (typeof window !== "undefined") {
    return "/api/v2/drug-dictionary"
  }
  return RXNAV_DISPLAY_NAMES
}

function parseDisplayTerms(json: unknown): string[] {
  const data = json as DisplayNamesResponse
  const terms = data.displayTermsList?.term
  if (!Array.isArray(terms)) {
    throw new Error("Unexpected RxNav displaynames response shape")
  }
  return terms.filter((t): t is string => typeof t === "string" && t.length > 0)
}

/** Load the full RxNorm display-term list (cached for the session). */
export async function loadDisplayTerms(): Promise<string[]> {
  if (displayTermsCache) return displayTermsCache
  if (displayTermsLoad) return displayTermsLoad

  displayTermsLoad = (async () => {
    const res = await fetch(getDictionaryUrl(), {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    })
    if (!res.ok) {
      throw new Error(`Drug dictionary fetch failed (${res.status})`)
    }
    const json: unknown = await res.json()
    const terms = parseDisplayTerms(json)
    displayTermsCache = terms
    return terms
  })()

  try {
    return await displayTermsLoad
  } catch (err) {
    displayTermsLoad = null
    throw err
  }
}

/** Case-insensitive prefix matches first, then substring; capped at `limit`. */
export function filterDisplayTerms(
  terms: readonly string[],
  query: string,
  limit = DEFAULT_SUGGESTION_LIMIT,
): string[] {
  const q = query.trim().toLowerCase()
  if (!q) return []

  const prefix: string[] = []
  const substring: string[] = []

  for (const term of terms) {
    const lc = term.toLowerCase()
    if (lc.startsWith(q)) {
      prefix.push(term)
      if (prefix.length >= limit) return prefix
    } else if (lc.includes(q)) {
      substring.push(term)
    }
  }

  const out = [...prefix]
  for (const term of substring) {
    if (out.length >= limit) break
    out.push(term)
  }
  return out
}

async function fetchRxcui(name: string): Promise<string | null> {
  const url = `${RXNAV_RXCUI}?name=${encodeURIComponent(name)}&search=2`
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  })
  if (!res.ok) return null
  const json = (await res.json()) as RxcuiResponse
  const id = json.idGroup?.rxnormId?.[0]
  return id ?? null
}

async function fetchIngredientName(rxcui: string): Promise<string | null> {
  const url = `${RXNAV_RELATED}/${encodeURIComponent(rxcui)}/related.json?tty=IN`
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  })
  if (!res.ok) return null
  const json = (await res.json()) as RelatedResponse
  const groups = json.relatedGroup?.conceptGroup ?? []
  for (const group of groups) {
    if (group.tty !== "IN") continue
    const name = group.conceptProperties?.[0]?.name?.trim()
    if (name) return name
  }
  return null
}

/**
 * Resolve a display term to a generic (ingredient) name. Best-effort — returns
 * the trimmed input when RxNav lookup fails.
 */
export async function resolveDisplayTermToGeneric(term: string): Promise<string> {
  const trimmed = term.trim()
  if (!trimmed) return trimmed

  try {
    const rxcui = await fetchRxcui(trimmed)
    if (!rxcui) return trimmed
    const ingredient = await fetchIngredientName(rxcui)
    return ingredient?.trim() || trimmed
  } catch {
    return trimmed
  }
}

/**
 * Pre-seed the display-term cache (e.g. from SSR on the public Trial Pipeline
 * tool where the authed v2 proxy is unavailable). Idempotent — first seed wins.
 */
export function seedDisplayTermsCache(terms: string[]): void {
  if (displayTermsCache?.length) return
  if (!terms.length) return
  displayTermsCache = terms
}

/** Test-only reset for module cache. */
export function _resetDrugDictionaryCacheForTests(): void {
  displayTermsCache = null
  displayTermsLoad = null
}

export const _internals = {
  parseDisplayTerms,
  filterDisplayTerms,
  DEFAULT_SUGGESTION_LIMIT,
  RXNAV_DISPLAY_NAMES,
}
